// =============================================================================
// BrandLogoPanel — krok "Logo i favicon" (faza Ulepszanie zaplecza).
//
//   Etap 1 „Zaproponuj prompt"  → Opus 4.8 pisze ANGIELSKI prompt + palete → EDYTOWALNY.
//   Etap 2 „Generuj logo"       → prompt zastyga (read-only, zapisany w map_brand_assets),
//                                 Recraft V4.1 Vector zwraca SVG do wyboru.
//   Po zapisaniu logo → komplet faviconow liczony lokalnie z tego SVG (0 zl) + paczka ZIP.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sparkles, Loader2, Wand2, Check, RotateCcw, Download, Package, Copy,
  AlertTriangle, Palette, Image as ImageIcon,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useBrandAssets } from "@/hooks/useBrandAssets";
import {
  buildBrandLogoSpec, generateLogoSvgs, domainFromUrl,
} from "@/services/uniquePages/brandLogoGenerator";
import { svgToDataUrl } from "@/lib/svgUtils";
import {
  buildFaviconBundle, bytesToDataUrl, downloadBytes, normalizeHexColor,
} from "@/lib/faviconBuilder";
import { createZip, type ZipEntry } from "@/lib/zipStore";
import type { ThematicMap } from "@/types/thematicMaps";
import type { RgbColor } from "@/types/brand";

const COUNT_OPTIONS = [2, 3, 4];

/** Kolejnosc i opisy plikow w sekcji pobierania. */
const FILE_LABELS: Record<string, string> = {
  "logo.svg": "Logo (wektor)",
  "favicon.svg": "Favicon SVG",
  "favicon.ico": "favicon.ico (16+32+48)",
  "apple-touch-icon.png": "Apple touch icon (180)",
  "icon-192.png": "PWA 192",
  "icon-512.png": "PWA 512",
  "site.webmanifest": "Manifest PWA",
  "head-snippet.html": "Snippet <head>",
};

function mimeFor(name: string): string {
  if (name.endsWith(".svg")) return "image/svg+xml";
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".ico")) return "image/x-icon";
  if (name.endsWith(".webmanifest")) return "application/manifest+json";
  return "text/html";
}

const rgbCss = (c: RgbColor) => `rgb(${c.r}, ${c.g}, ${c.b})`;

/** Szachownica pod podgladem — od razu widac, czy tlo zostalo zdjete. */
const CHECKER =
  "bg-[linear-gradient(45deg,#00000010_25%,transparent_25%),linear-gradient(-45deg,#00000010_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#00000010_75%),linear-gradient(-45deg,transparent_75%,#00000010_75%)] bg-[length:12px_12px] bg-[position:0_0,0_6px,6px_-6px,-6px_0px]";

/** onProgress — sygnał do steppera, że krok właśnie się „zrobił" (zielona kropka bez przeładowania). */
export function BrandLogoPanel({ map, onProgress }: { map: ThematicMap; onProgress?: () => void }) {
  const { toast } = useToast();
  const { assets, save, saving, clear } = useBrandAssets(map.id);

  const [draftPrompt, setDraftPrompt] = useState<string | null>(null);
  const [draftColors, setDraftColors] = useState<RgbColor[]>([]);
  const [draftTheme, setDraftTheme] = useState("#142850");
  const [proposals, setProposals] = useState<string[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [count, setCount] = useState(3);
  const [building, setBuilding] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [bundle, setBundle] = useState<ZipEntry[] | null>(null);
  const [bundleError, setBundleError] = useState("");

  const savedPrompt = assets?.logo_prompt ?? null;
  const savedLogo = assets?.logo_svg ?? null;
  const themeColor = normalizeHexColor(assets?.theme_color ?? draftTheme);
  const busy = building || generating || saving;

  // Paczka faviconow — liczona raz na zapisane logo, potem tylko pobierana
  useEffect(() => {
    let cancelled = false;
    if (!savedLogo) { setBundle(null); return; }
    setBundleError("");
    (async () => {
      try {
        const files = await buildFaviconBundle(savedLogo, themeColor);
        if (!cancelled) setBundle(files);
      } catch (e) {
        if (!cancelled) setBundleError(e instanceof Error ? e.message : "Nie udalo sie zbudować faviconów");
      }
    })();
    return () => { cancelled = true; };
  }, [savedLogo, themeColor]);

  const handleBuildPrompt = async () => {
    setBuilding(true);
    try {
      const res = await buildBrandLogoSpec(map.id);
      setDraftPrompt(res.spec.prompt);
      setDraftColors(res.spec.colors);
      setDraftTheme(res.spec.theme_color);
      if (res.source === "fallback") {
        toast({
          title: "Użyto promptu zapasowego",
          description: "Model nie zwrócił poprawnej odpowiedzi — możesz poprawić prompt ręcznie przed generowaniem.",
        });
      }
    } catch (e) {
      toast({
        title: "Nie udało się zbudować promptu",
        description: e instanceof Error ? e.message : "Nieznany błąd",
        variant: "destructive",
      });
    } finally {
      setBuilding(false);
    }
  };

  const handleGenerate = async () => {
    const prompt = (savedPrompt ?? draftPrompt ?? "").trim();
    if (!prompt) return;
    setGenerating(true);
    setProposals([]);
    setSelected(null);
    try {
      // Prompt zapisujemy DOPIERO tutaj — czyli po ewentualnej ręcznej edycji.
      if (!savedPrompt) {
        await save({ logo_prompt: prompt, theme_color: draftTheme, colors: draftColors });
      }
      const res = await generateLogoSvgs({
        prompt,
        colors: assets?.colors?.length ? assets.colors : draftColors,
        count,
        onSvg: (svg) => setProposals((p) => [...p, svg]),
      });
      if (res.ok === 0) {
        toast({
          title: "Nie wygenerowano żadnego logo",
          description: res.errors[0] || "Recraft nie zwrócił pliku SVG.",
          variant: "destructive",
        });
      } else if (res.failed > 0) {
        toast({ title: `Gotowe ${res.ok} z ${count}`, description: res.errors[0] });
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveLogo = async () => {
    if (selected === null || !proposals[selected]) return;
    await save({ logo_svg: proposals[selected] });
    setProposals([]);
    setSelected(null);
    onProgress?.();
    toast({ title: "Logo zapisane", description: "Favicony wyliczą się automatycznie z tego wektora." });
  };

  const handleClear = async () => {
    setDraftPrompt(null);
    setDraftColors([]);
    setProposals([]);
    setSelected(null);
    await clear();
    onProgress?.();
  };

  const zipName = useMemo(() => {
    const d = domainFromUrl((map as any).website_url || "") || (map.name || "brand");
    return `${d.replace(/[^a-z0-9.-]+/gi, "-").toLowerCase()}-logo-favicony.zip`;
  }, [map]);

  const handleDownloadZip = useCallback(() => {
    if (!bundle) return;
    downloadBytes(createZip(bundle), zipName, "application/zip");
  }, [bundle, zipName]);

  const handleCopySnippet = useCallback(async () => {
    const entry = bundle?.find((f) => f.name === "head-snippet.html");
    if (!entry) return;
    await navigator.clipboard.writeText(new TextDecoder().decode(entry.bytes));
    toast({ title: "Snippet skopiowany" });
  }, [bundle, toast]);

  const headSnippet = useMemo(() => {
    const entry = bundle?.find((f) => f.name === "head-snippet.html");
    return entry ? new TextDecoder().decode(entry.bytes) : "";
  }, [bundle]);

  const faviconPreviews = useMemo(
    () =>
      (bundle ?? [])
        .filter((f) => /^favicon-\d+x\d+\.png$/.test(f.name))
        .map((f) => ({
          size: Number(f.name.match(/(\d+)x/)?.[1] || 0),
          dataUrl: bytesToDataUrl(f.bytes, "image/png"),
        })),
    [bundle],
  );

  return (
    <div className="space-y-4">
      <div>
        <h3 className="flex items-center gap-2 text-base font-semibold">
          <Palette className="h-4 w-4" />
          Logo i favicon
        </h3>
        <p className="text-sm text-muted-foreground">
          Krok 1: model Opus 4.8 pisze angielski prompt na sygnet (z tematyki, odbiorców i opisu serwisu) — możesz go
          poprawić. Krok 2: Recraft V4.1 zwraca <span className="font-medium">prawdziwy SVG</span>. Z wybranego wektora
          panel sam wycina komplet faviconów — bez dodatkowych kosztów AI.
        </p>
      </div>

      {/* --- Stan A: nic jeszcze nie ma ------------------------------------ */}
      {!savedLogo && !savedPrompt && draftPrompt === null && (
        <Button size="sm" onClick={handleBuildPrompt} disabled={busy}>
          {building ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Wand2 className="mr-1.5 h-3.5 w-3.5" />}
          1. Zaproponuj prompt (Opus 4.8)
        </Button>
      )}

      {/* --- Stan B: draft promptu — edytowalny ---------------------------- */}
      {!savedLogo && !savedPrompt && draftPrompt !== null && (
        <div className="space-y-2 rounded-md border border-border/60 p-3">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              Prompt po angielsku (możesz poprawić przed generowaniem)
            </span>
            <Badge variant="outline" className="text-[10px]">edytowalny</Badge>
          </div>
          <Textarea
            value={draftPrompt}
            onChange={(e) => setDraftPrompt(e.target.value)}
            className="min-h-[110px] font-mono text-xs"
            disabled={busy}
          />

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground">Paleta</span>
              {draftColors.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
              {draftColors.map((c, i) => (
                <span
                  key={i}
                  className="h-5 w-5 rounded border border-border/60"
                  style={{ backgroundColor: rgbCss(c) }}
                  title={rgbCss(c)}
                />
              ))}
            </div>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              theme-color
              <input
                type="color"
                value={normalizeHexColor(draftTheme)}
                onChange={(e) => setDraftTheme(e.target.value)}
                className="h-6 w-10 cursor-pointer rounded border border-border/60 bg-transparent"
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Select value={String(count)} onValueChange={(v) => setCount(Number(v))} disabled={busy}>
              <SelectTrigger className="h-8 w-[130px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {COUNT_OPTIONS.map((n) => (
                  <SelectItem key={n} value={String(n)} className="text-xs">{n} propozycje</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={handleGenerate} disabled={busy || !draftPrompt.trim()}>
              {generating ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
              2. Generuj logo
            </Button>
            <Button size="sm" variant="ghost" className="text-xs" onClick={() => setDraftPrompt(null)} disabled={busy}>
              Wyczyść
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Po kliknięciu „Generuj logo" prompt zostaje zapisany i przestaje być edytowalny. Koszt: ok. $0,08 za sztukę.
          </p>
        </div>
      )}

      {/* --- Stan C: prompt użyty (read-only) + propozycje ------------------ */}
      {!savedLogo && savedPrompt && (
        <div className="space-y-2 rounded-md border border-border/60 p-3">
          <PromptReadOnly prompt={savedPrompt} />
          <div className="flex flex-wrap items-center gap-2">
            <Select value={String(count)} onValueChange={(v) => setCount(Number(v))} disabled={busy}>
              <SelectTrigger className="h-8 w-[130px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {COUNT_OPTIONS.map((n) => (
                  <SelectItem key={n} value={String(n)} className="text-xs">{n} propozycje</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={handleGenerate} disabled={busy}>
              {generating ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
              {proposals.length ? "Generuj ponownie" : "Generuj logo"}
            </Button>
            <Button size="sm" variant="ghost" className="text-xs" onClick={handleClear} disabled={busy}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />Wyczyść prompt
            </Button>
          </div>

          {(generating || proposals.length > 0) && (
            <div className="space-y-2 pt-1">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                  {generating ? `Propozycje (${proposals.length}/${count} gotowych…)` : "Wybierz jedno logo"}
                </span>
                {proposals.length > 0 && (
                  <Button size="sm" onClick={handleSaveLogo} disabled={busy || selected === null}>
                    {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1.5 h-3.5 w-3.5" />}
                    Zapisz jako logo mapy
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {proposals.map((svg, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSelected(i)}
                    disabled={saving}
                    className={`relative overflow-hidden rounded-md border-2 transition-colors ${
                      selected === i ? "border-primary" : "border-border/60 hover:border-primary/40"
                    }`}
                  >
                    <div className={`flex aspect-square items-center justify-center p-3 ${CHECKER}`}>
                      <img src={svgToDataUrl(svg)} alt={`Logo ${i + 1}`} className="h-full w-full object-contain" />
                    </div>
                    {selected === i && (
                      <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="h-3 w-3" />
                      </span>
                    )}
                  </button>
                ))}
                {generating &&
                  Array.from({ length: Math.max(0, count - proposals.length) }).map((_, i) => (
                    <div key={`sk-${i}`} className="flex aspect-square items-center justify-center rounded-md border border-dashed border-border/60">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  ))}
              </div>

              {!generating && proposals.length > 0 && (
                <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <AlertTriangle className="h-3 w-3" />
                  Szachownica pokazuje przezroczystość — niezapisane propozycje znikną po odświeżeniu strony.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* --- Stan D: logo zapisane → favicony + pobieranie ------------------ */}
      {savedLogo && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-3 rounded-md border border-border/60 p-3">
            <div className="flex items-center justify-between">
              <h4 className="flex items-center gap-2 text-sm font-semibold"><ImageIcon className="h-4 w-4" />Logo mapy</h4>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={handleClear} disabled={busy}>
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />Wyczyść logo
              </Button>
            </div>
            <div className={`flex aspect-square w-full max-w-[260px] items-center justify-center rounded-md border border-border/60 p-4 ${CHECKER}`}>
              <img src={svgToDataUrl(savedLogo)} alt="Logo" className="h-full w-full object-contain" />
            </div>
            {savedPrompt && <PromptReadOnly prompt={savedPrompt} />}
          </div>

          <div className="space-y-3 rounded-md border border-border/60 p-3">
            <h4 className="flex items-center gap-2 text-sm font-semibold"><Package className="h-4 w-4" />Favicony</h4>

            {bundleError ? (
              <p className="flex items-center gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                <AlertTriangle className="h-3.5 w-3.5" />{bundleError}
              </p>
            ) : !bundle ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />Rasteryzuję ikony…
              </p>
            ) : (
              <>
                <div className="flex items-end gap-3">
                  {faviconPreviews.map((p) => (
                    <div key={p.size} className="flex flex-col items-center gap-1">
                      <img src={p.dataUrl} alt={`favicon ${p.size}`} style={{ width: p.size, height: p.size }} className="rounded-sm border border-border/40 bg-white" />
                      <span className="text-[10px] text-muted-foreground">{p.size}px</span>
                    </div>
                  ))}
                  <span className="pb-4 text-[11px] text-muted-foreground">← czytelne w 16px?</span>
                </div>

                <Button size="sm" onClick={handleDownloadZip}>
                  <Package className="mr-1.5 h-3.5 w-3.5" />Pobierz paczkę (ZIP)
                </Button>

                <div className="flex flex-wrap gap-1.5">
                  {bundle
                    .filter((f) => FILE_LABELS[f.name])
                    .map((f) => (
                      <Button
                        key={f.name}
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => downloadBytes(f.bytes, f.name, mimeFor(f.name))}
                        title={f.name}
                      >
                        <Download className="mr-1 h-3 w-3" />{FILE_LABELS[f.name]}
                      </Button>
                    ))}
                </div>

                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">Do wklejenia w &lt;head&gt;</span>
                    <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={handleCopySnippet}>
                      <Copy className="mr-1 h-3 w-3" />Kopiuj
                    </Button>
                  </div>
                  <pre className="max-h-32 overflow-auto rounded bg-muted px-2 py-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                    {headSnippet}
                  </pre>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Prompt użyty do generowania — pokazany obok efektu, celowo NIE do edycji. */
function PromptReadOnly({ prompt }: { prompt: string }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-medium text-muted-foreground">Użyty prompt</span>
        <Badge variant="outline" className="text-[10px] text-muted-foreground">tylko do odczytu</Badge>
      </div>
      <p className="max-h-32 overflow-y-auto whitespace-pre-wrap rounded bg-muted px-2 py-1.5 font-mono text-[11px] leading-relaxed text-muted-foreground">
        {prompt}
      </p>
    </div>
  );
}

export default BrandLogoPanel;
